import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// ---------- Types ----------

interface PaginatedResponse<TItem, TSummary = unknown> {
  items: TItem[];
  nextCursor?: string;
  total?: number;
  summary?: TSummary;
}

interface UsePaginatedQueryOptions<TItem, TResponse, TSummary = undefined> {
  /** Prefix for the React Query key, e.g. 'events-new' */
  queryKey: string;

  /**
   * Build the full query params object. Called with the current cursor.
   * When cursor is defined, should return { cursor, limit }.
   * When cursor is undefined, should return { ...allFilters, limit }.
   * MUST be wrapped in useCallback by the caller.
   */
  buildQueryParams: (cursor: string | undefined) => Record<string, any>;

  /** The API call function. Receives the queryParams returned by buildQueryParams. */
  queryFn: (params: Record<string, any>) => Promise<TResponse>;

  /**
   * Map the raw API response to the normalized shape the hook needs.
   * Default handles the standard { data, pagination, summary } shape.
   */
  mapResponse?: (response: TResponse) => PaginatedResponse<TItem, TSummary>;

  /** Whether the query is enabled. Defaults to true. */
  enabled?: boolean;

  /**
   * Row identity for cross-page duplicate detection and updateItem matching.
   * Pass the SAME function the page passes to DataTable's getRowKey, so the
   * hook and the table agree on what makes a row unique. A duplicate key
   * across pages is a backend cursor bug (the libs/cursor contract is that
   * each page is strictly past the previous one) — the hook surfaces it as
   * an error rather than silently dropping rows.
   */
  getRowKey: (item: TItem) => string;
}

interface UsePaginatedQueryResult<TItem, TSummary = undefined> {
  /** All accumulated items across all loaded pages */
  allItems: TItem[];
  /** Total count from the API (first page only) */
  totalCount: number | undefined;
  /** API summary from the first page */
  apiSummary: TSummary | undefined;
  /** Whether the initial page is loading */
  isLoading: boolean;
  /** Query error from useQuery, OR a client-side error such as a duplicate
   *  row key surfaced from cross-page detection. */
  error: Error | null;
  /** Whether there are more pages to load */
  hasMore: boolean;
  /** Call to load the next page */
  handleLoadMore: () => void;
  /** Manually update an item in the accumulated list (for optimistic updates) */
  updateItem: (id: string, updater: (item: TItem) => TItem) => void;
  /** Manually set the full items array */
  setAllItems: React.Dispatch<React.SetStateAction<TItem[]>>;
  /** Clear accumulated state and the next-cursor pointer; pair with a
   *  queryClient.invalidateQueries() to force a fresh first-page fetch
   *  after a backend mutation that changed already-rendered rows. */
  reset: () => void;
}

// ---------- Default mapResponse for standard { data, pagination, summary } shape ----------

function defaultMapResponse<TItem, TSummary>(response: any): PaginatedResponse<TItem, TSummary> {
  return {
    items: response?.data ?? [],
    nextCursor: response?.pagination?.cursor,
    total: response?.pagination?.total,
    summary: response?.summary,
  };
}

// ---------- Hook ----------

export function usePaginatedQuery<TItem, TResponse, TSummary = undefined>(
  options: UsePaginatedQueryOptions<TItem, TResponse, TSummary>
): UsePaginatedQueryResult<TItem, TSummary> {
  const {
    queryKey: keyPrefix,
    buildQueryParams,
    queryFn,
    mapResponse = defaultMapResponse as (response: TResponse) => PaginatedResponse<TItem, TSummary>,
    enabled = true,
    getRowKey,
  } = options;

  // getRowKey is usually passed inline, so it's a new identity every render.
  // Hold it in a ref: the accumulate effect keys off page data, NOT getRowKey
  // identity — otherwise every render re-runs the effect and its first-page
  // setAllItems(mapped.items) clobbers optimistic updateItem() patches.
  const getRowKeyRef = useRef(getRowKey);
  getRowKeyRef.current = getRowKey;

  // --- Pagination state ---
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allItems, setAllItems] = useState<TItem[]>([]);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [apiSummary, setApiSummary] = useState<TSummary | undefined>(undefined);
  const [duplicateKeyError, setDuplicateKeyError] = useState<Error | null>(null);
  const isLoadingMoreRef = useRef(false);
  const nextCursorRef = useRef<string | undefined>(undefined);

  // Which cursor's page is already folded into allItems, and how many items
  // preceded it. The accumulate effect keys off `mapped`, whose identity changes
  // on any refetch — including one triggered by invalidateQueries against the
  // page currently being observed. Without this, a refetch of the page we
  // already consumed appends it a second time, which the duplicate-key guard
  // then reports as `cursor returned duplicate row key ... already seen on a
  // prior page`, blaming the backend for a client-side re-append.
  //
  // Lengths are tracked here rather than read from allItems so the bookkeeping
  // doesn't depend on when React flushes the state update.
  const consumedCursorRef = useRef<string | undefined>(undefined);
  const consumedAtLengthRef = useRef(0);
  const accumulatedLengthRef = useRef(0);

  // --- Build query params ---
  const queryParams = useMemo(
    () => buildQueryParams(cursor),
    [buildQueryParams, cursor]
  );

  // page1Params: what buildQueryParams returns with no cursor. Used to detect filter/sort changes.
  const page1Params = useMemo(
    () => buildQueryParams(undefined),
    [buildQueryParams]
  );

  // --- React Query ---
  const queryClient = useQueryClient();
  const { data: currentPage, isLoading, error: queryError } = useQuery({
    queryKey: [keyPrefix, queryParams],
    queryFn: () => queryFn(queryParams),
    enabled,
  });

  // Surface either the network/query error or a client-side duplicate-key
  // error through the same field — duplicate-key wins because if it fired,
  // accumulated state is intentionally not advanced.
  const error = duplicateKeyError ?? (queryError as Error | null);

  // --- Memoize mapped response to avoid calling mapResponse in multiple effects ---
  const mapped = useMemo(() => {
    if (!currentPage) return null;
    return mapResponse(currentPage);
  }, [currentPage, mapResponse]);

  // --- Track next cursor ---
  useEffect(() => {
    if (!mapped) return;
    nextCursorRef.current = mapped.nextCursor;
  }, [mapped]);

  // --- Accumulate data when new page arrives ---
  useEffect(() => {
    if (!mapped || mapped.items.length === 0) {
      if (mapped && cursor === undefined) {
        // First page returned empty - clear everything
        setAllItems([]);
        setTotalCount(mapped.total);
        setApiSummary(mapped.summary);
      }
      isLoadingMoreRef.current = false;
      return;
    }

    if (cursor === undefined) {
      // First page: replace. Detect duplicates inside the page itself —
      // a single page with two rows sharing a getRowKey is a backend bug
      // (or a wrong getRowKey) and would crash React's key invariant
      // anyway; surface it as an error.
      const seen = new Set<string>();
      for (const item of mapped.items) {
        const key = getRowKeyRef.current(item);
        if (seen.has(key)) {
          setDuplicateKeyError(new Error(`paginated query "${keyPrefix}": duplicate row key "${key}" within first page`));
          isLoadingMoreRef.current = false;
          return;
        }
        seen.add(key);
      }
      setAllItems(mapped.items);
      setTotalCount(mapped.total);
      setApiSummary(mapped.summary);
      consumedCursorRef.current = undefined;
      consumedAtLengthRef.current = 0;
      accumulatedLengthRef.current = mapped.items.length;
    } else {
      // Subsequent page: detect duplicates against the accumulated set.
      // The libs/cursor contract is that each page is strictly past the
      // previous one — a duplicate means the cursor is broken. Set error
      // state instead of silently dropping rows.
      // A repeat of the cursor we already consumed is a refetch of that same
      // page, not a new one — replace its rows in place so fresher server data
      // lands without duplicating them.
      const isRefetchOfConsumedPage = consumedCursorRef.current === cursor;
      const baseLength = isRefetchOfConsumedPage
        ? consumedAtLengthRef.current
        : accumulatedLengthRef.current;

      setAllItems(prev => {
        const base = prev.slice(0, baseLength);
        const existingKeys = new Set(base.map(getRowKeyRef.current));
        for (const item of mapped.items) {
          const key = getRowKeyRef.current(item);
          if (existingKeys.has(key)) {
            setDuplicateKeyError(new Error(`paginated query "${keyPrefix}": cursor returned duplicate row key "${key}" already seen on a prior page`));
            return prev;
          }
          existingKeys.add(key);
        }
        return [...base, ...mapped.items];
      });

      consumedCursorRef.current = cursor;
      consumedAtLengthRef.current = baseLength;
      accumulatedLengthRef.current = baseLength + mapped.items.length;
    }

    isLoadingMoreRef.current = false;
  }, [mapped, cursor, keyPrefix]);

  // --- Reset on filter/sort change ---
  // page1Params (built from buildQueryParams) is the canonical signal that
  // the user changed a filter/sort. When it changes we have to do BOTH:
  //   1. Wipe the local accumulator so stale rows don't render under the
  //      new filter while the new fetch is in flight.
  //   2. Invalidate the react-query cache for this prefix. Without this,
  //      flipping a filter back to a previously-fetched value (e.g. clear
  //      the chip after typing in it) returns the cached page synchronously
  //      with no network call — and because mapResponse output may be
  //      reference-stable, the accumulator effect's deps don't change and
  //      we never re-populate `allItems` after the wipe in step 1. Result:
  //      the table goes blank until the user does something else.
  // invalidateQueries (rather than removeQueries) is intentional: it marks
  // every matching query stale AND triggers a refetch on active observers,
  // which is what we want here. removeQueries clears the cache but doesn't
  // reliably re-fire active subscribers.
  // prevPage1ParamsRef is initialized to JSON.stringify(page1Params) on
  // mount. The very first effect run sees serialized === prev → skips
  // (correct: no need to reset what we're already displaying). Every
  // subsequent change to page1Params (filter / sort / date range) sees
  // serialized !== prev → fires the reset.
  //
  // Historical bug: an extra `isInitialMount` guard swallowed the FIRST
  // genuine page1Params change after mount (the first date-nav click,
  // first sort switch, etc.), leaving allItems / apiSummary / cursor
  // stuck on the OLD filter set while the URL + filter UI updated to
  // the new one. The user saw stale rows under a fresh-looking header.
  // The redundant guard is gone.
  const prevPage1ParamsRef = useRef<string>(JSON.stringify(page1Params));

  useEffect(() => {
    const serialized = JSON.stringify(page1Params);
    if (serialized === prevPage1ParamsRef.current) return;
    prevPage1ParamsRef.current = serialized;

    setCursor(undefined);
    setAllItems([]);
    setTotalCount(undefined);
    setApiSummary(undefined);
    setDuplicateKeyError(null);
    isLoadingMoreRef.current = false;
    nextCursorRef.current = undefined;
    consumedCursorRef.current = undefined;
    consumedAtLengthRef.current = 0;
    accumulatedLengthRef.current = 0;
    queryClient.invalidateQueries({ queryKey: [keyPrefix] });
  }, [page1Params, queryClient, keyPrefix]);

  // --- Load more ---
  const handleLoadMore = useCallback(() => {
    if (
      !isLoadingMoreRef.current &&
      nextCursorRef.current &&
      nextCursorRef.current !== cursor
    ) {
      isLoadingMoreRef.current = true;
      setCursor(nextCursorRef.current);
    }
  }, [cursor]);

  // --- Derived ---
  const hasMore = !!nextCursorRef.current;

  // --- Imperative helpers ---
  // updateItem matches by getRowKey — pass the same string the row would
  // produce (e.g. a composite key like "<id>:<role>:<name>" when the page
  // uses a composite getRowKey).
  const updateItem = useCallback((id: string, updater: (item: TItem) => TItem) => {
    setAllItems(prev => prev.map(item =>
      getRowKeyRef.current(item) === id ? updater(item) : item
    ));
  }, []);

  // reset clears every accumulated page and the next-cursor pointer,
  // returning the hook to "fetching page 1 from scratch" state. Also
  // wipes the react-query cache for this hook's key prefix so the
  // next fetch can't structurally-share with stale data — without
  // that wipe, a mutation that produces a byte-identical response
  // (same row still flagged after a revalidate, same row still
  // present after a no-op edit) keeps the prior `currentPage`
  // reference, the accumulator useEffect's deps don't change, and
  // it never refires to repopulate the freshly-cleared allItems —
  // table renders empty until a full reload.
  const reset = useCallback(() => {
    queryClient.removeQueries({ queryKey: [keyPrefix] });
    setCursor(undefined);
    setAllItems([]);
    setTotalCount(undefined);
    setApiSummary(undefined);
    setDuplicateKeyError(null);
    isLoadingMoreRef.current = false;
    nextCursorRef.current = undefined;
    consumedCursorRef.current = undefined;
    consumedAtLengthRef.current = 0;
    accumulatedLengthRef.current = 0;
  }, [queryClient, keyPrefix]);

  return {
    allItems,
    totalCount,
    apiSummary,
    isLoading,
    error,
    hasMore,
    handleLoadMore,
    updateItem,
    setAllItems,
    reset,
  };
}
